import api from 'config/api.config';
import { toast } from 'react-toastify';

export interface AccountProfile {
  username: string;
  full_name: string;
  bio: string;
  followers_count: number;
  following_count: number;
  media_count: number;
  is_verified: boolean;
  category: string;
}

export interface ChatMessage {
  role: 'assistant' | 'user';
  content: string;
  timestamp: string;
}

export interface RuleTarget {
  type: string;
  filter: string;
  id?: number;
}

export interface ProposedRules {
  rule_name: string;
  rule: RuleTarget[];
  description: string;
  confirmed: boolean | null;
  created_rule_id: string | null;
}

export interface StartSessionResponse {
  session_id: string;
  message: ChatMessage | null;
  account_analysis: AccountProfile;
  streamed: boolean;
}

export interface SendMessageResponse {
  message: ChatMessage | null;
  proposed_rules: ProposedRules | null;
  status: 'active' | 'rules_proposed' | 'rules_created' | 'completed' | 'expired';
  streamed: boolean;
}

export interface ConfirmRulesResponse {
  message: string;
  rule_id?: string;
  rule_name?: string;
  target_count?: number;
}

export const startSession = async (social_media_account_id: string) => {
  try {
    const response: any = await api.post('/api/chatbot/start-session', { social_media_account_id }, { timeout: 180000 });
    return response;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to start chatbot session';
    toast.error(message);
    if (error.response) {
      return error.response;
    }
    throw new Error(message);
  }
};

export const sendChatMessage = async (session_id: string, message: string) => {
  try {
    const response: any = await api.post('/api/chatbot/message', { session_id, message }, { timeout: 180000 });
    return response;
  } catch (error: any) {
    const msg = error.response?.data?.message || 'Failed to send message';
    toast.error(msg);
    if (error.response) {
      return error.response;
    }
    throw new Error(msg);
  }
};

export const confirmRules = async (session_id: string, confirmed: boolean) => {
  try {
    const response: any = await api.post('/api/chatbot/confirm-rules', { session_id, confirmed });
    return response;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to confirm rules';
    toast.error(message);
    if (error.response) {
      return error.response;
    }
    throw new Error(message);
  }
};

export const getSession = async (session_id: string) => {
  try {
    const response: any = await api.get(`/api/chatbot/session/${session_id}`);
    return response;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to fetch session';
    toast.error(message);
    if (error.response) {
      return error.response;
    }
    throw new Error(message);
  }
};

export const getSessions = async (social_media_account_id: string) => {
  try {
    const response: any = await api.get(`/api/chatbot/sessions/${social_media_account_id}`);
    return response;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to fetch sessions';
    toast.error(message);
    if (error.response) {
      return error.response;
    }
    throw new Error(message);
  }
};

export const deleteSession = async (session_id: string) => {
  try {
    const response: any = await api.delete(`/api/chatbot/session/${session_id}`);
    return response;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to delete session';
    toast.error(message);
    if (error.response) {
      return error.response;
    }
    throw new Error(message);
  }
};
